import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Camera,
  PlayCircle,
  ShieldAlert,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { Stepper, type StepItem } from '@/components/ui/Stepper';
import { RolloutPlanTable } from '@/components/runs/RolloutPlanTable';
import { RolloutResult } from '@/components/runs/RolloutResult';
import {
  BELEG_BEISPIEL,
  VERZICHT_BEISPIEL,
  eineZeile,
} from '@/components/runs/snapshotWaiver';
import { RoutineTierBadge } from '@/components/routines/tier';
import { useCustomers } from '@/hooks/useCustomers';
import { compareRoutineOrder, useRoutines, type Routine } from '@/hooks/useRoutines';
import {
  useRolloutPreview,
  useStartRollout,
  type RolloutPreviewInput,
  type RolloutPreviewResponse,
  type RolloutResponse,
} from '@/hooks/useRollout';
import type { Customer } from '@/types/api';

/**
 * /runs/rollout - kundenweite Ausrollung.
 *
 * Reihenfolge je Kunde folgt execution_order der Routinen; die Kunden
 * selbst laufen nacheinander in der Prioritätsliste (oder alphabetisch,
 * wenn "Alle Kunden" an ist). Ein Run bleibt ein Playbook eines Kunden -
 * das Backend legt deshalb N Läufe an, das UI führt sie als einen Vorgang.
 */

export const Route = createFileRoute('/runs/rollout')({
  component: RolloutPage,
});

type StepId = 'routines' | 'customers' | 'start';

const STEPS: StepItem<StepId>[] = [
  { id: 'routines', label: 'Routinen', description: 'Was läuft?' },
  { id: 'customers', label: 'Kunden', description: 'Bei wem, in welcher Reihenfolge?' },
  { id: 'start', label: 'Start', description: 'Vorschau und starten' },
];

function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `rollout-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function parseSlugList(raw: string): string[] {
  return raw
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function RolloutPage(): JSX.Element {
  const routinesQuery = useRoutines();
  const customersQuery = useCustomers({ limit: 200 });
  const previewMut = useRolloutPreview();
  const startMut = useStartRollout();

  const [step, setStep] = useState<StepId>('routines');
  const [completed, setCompleted] = useState<StepId[]>([]);
  const [selectedRoutineSlugs, setSelectedRoutineSlugs] = useState<string[]>([]);
  const [allCustomers, setAllCustomers] = useState(false);
  const [prioritySlugs, setPrioritySlugs] = useState<string[]>([]);
  const [excludeCustomerRaw, setExcludeCustomerRaw] = useState('');
  const [excludeSystemIds, setExcludeSystemIds] = useState<string[]>([]);
  const [checkMode, setCheckMode] = useState(true);
  const [snapshot, setSnapshot] = useState(true);
  const [waiverReason, setWaiverReason] = useState('');
  const [waiverEvidence, setWaiverEvidence] = useState('');
  const [idempotencyKey] = useState(() => generateIdempotencyKey());
  const [preview, setPreview] = useState<RolloutPreviewResponse | null>(null);
  const [result, setResult] = useState<RolloutResponse | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const routines = useMemo(() => {
    const list = [...(routinesQuery.data?.data ?? [])];
    list.sort(compareRoutineOrder);
    return list;
  }, [routinesQuery.data]);

  const activeCustomers = useMemo(() => {
    const list = (customersQuery.data?.data ?? []).filter((c) => !c.deactivated_at);
    list.sort((a, b) => a.name.localeCompare(b.name, 'de'));
    return list;
  }, [customersQuery.data]);

  const customerBySlug = useMemo(() => {
    const m = new Map<string, Customer>();
    for (const c of activeCustomers) m.set(c.slug, c);
    return m;
  }, [activeCustomers]);

  const selectedRoutines = useMemo(
    () => routines.filter((r) => selectedRoutineSlugs.includes(r.code)),
    [routines, selectedRoutineSlugs]
  );

  const destructive = selectedRoutines.some((r) => r.is_destructive);
  const requiresSnapshot = selectedRoutines.some((r) => r.requires_snapshot);

  const previewInput = (): RolloutPreviewInput => {
    const orderedSlugs = [...selectedRoutines]
      .sort(compareRoutineOrder)
      .map((r) => r.code);
    const input: RolloutPreviewInput = { routineSlugs: orderedSlugs };
    input.customerSlugs = allCustomers ? null : prioritySlugs;
    const excludedCustomers = parseSlugList(excludeCustomerRaw);
    if (excludedCustomers.length > 0) input.excludeCustomerSlugs = excludedCustomers;
    if (excludeSystemIds.length > 0) input.excludeSystemIds = excludeSystemIds;
    return input;
  };

  const canPreview =
    selectedRoutineSlugs.length > 0 && (allCustomers || prioritySlugs.length > 0);

  const loadPreview = (): void => {
    if (!canPreview) return;
    previewMut.mutate(previewInput(), {
      onSuccess: (data) => {
        setPreview(data);
        setResult(null);
      },
    });
  };

  const toggleExcludeSystem = (systemId: string): void => {
    const next = excludeSystemIds.includes(systemId)
      ? excludeSystemIds.filter((id) => id !== systemId)
      : [...excludeSystemIds, systemId];
    setExcludeSystemIds(next);
    // Vorschau muss denselben Stand zeigen, sonst bleibt ein ausgenommenes
    // System in der Tabelle stehen und der Start läuft mit alten IDs.
    const orderedSlugs = [...selectedRoutines]
      .sort(compareRoutineOrder)
      .map((r) => r.code);
    const input: RolloutPreviewInput = { routineSlugs: orderedSlugs };
    input.customerSlugs = allCustomers ? null : prioritySlugs;
    const excludedCustomers = parseSlugList(excludeCustomerRaw);
    if (excludedCustomers.length > 0) input.excludeCustomerSlugs = excludedCustomers;
    if (next.length > 0) input.excludeSystemIds = next;
    previewMut.mutate(input, {
      onSuccess: (data) => {
        setPreview(data);
        setResult(null);
      },
    });
  };

  const startRollout = (asCheck: boolean): void => {
    if (!preview) return;
    const runSnapshot = asCheck ? false : snapshot;
    const runWaiver =
      !asCheck && !snapshot && (requiresSnapshot || destructive);
    startMut.mutate(
      {
        ...previewInput(),
        checkMode: asCheck,
        snapshot: runSnapshot,
        ...(runWaiver
          ? {
              // Ohne Begründung fällt der Server auf snapshot_override zurück.
              ...(eineZeile(waiverReason) !== ''
                ? { snapshotWaiverReason: eineZeile(waiverReason) }
                : { snapshotOverride: true }),
              ...(eineZeile(waiverEvidence) !== ''
                ? { snapshotWaiverEvidence: eineZeile(waiverEvidence) }
                : {}),
            }
          : {}),
        idempotencyKey,
      },
      {
        onSuccess: (res) => {
          setResult(res);
          setConfirmOpen(false);
        },
      }
    );
  };

  const markDone = (id: StepId): void =>
    setCompleted((c) => (c.includes(id) ? c : [...c, id]));

  const goNext = (): void => {
    markDone(step);
    const idx = STEPS.findIndex((s) => s.id === step);
    const next = STEPS[idx + 1];
    if (next) setStep(next.id);
  };

  const goPrev = (): void => {
    const idx = STEPS.findIndex((s) => s.id === step);
    const prev = STEPS[idx - 1];
    if (prev) setStep(prev.id);
  };

  const toggleRoutine = (code: string): void => {
    setSelectedRoutineSlugs((cur) =>
      cur.includes(code) ? cur.filter((s) => s !== code) : [...cur, code]
    );
    setPreview(null);
  };

  const toggleCustomer = (slug: string): void => {
    setPrioritySlugs((cur) =>
      cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug]
    );
    setPreview(null);
  };

  const movePriority = (slug: string, dir: -1 | 1): void => {
    setPrioritySlugs((cur) => {
      const idx = cur.indexOf(slug);
      const nextIdx = idx + dir;
      if (idx < 0 || nextIdx < 0 || nextIdx >= cur.length) return cur;
      const copy = [...cur];
      const a = copy[idx];
      const b = copy[nextIdx];
      if (a === undefined || b === undefined) return cur;
      copy[idx] = b;
      copy[nextIdx] = a;
      return copy;
    });
    setPreview(null);
  };

  if (result) {
    return (
      <div className="flex flex-col gap-4">
        <header>
          <h1 className="text-2xl font-semibold">Rollout</h1>
          <p className="text-muted">Kundenweite Ausrollung.</p>
        </header>
        <RolloutResult result={result} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">Rollout</h1>
        <p className="text-muted">
          Eine Ausrollung ueber alle (oder ausgewaehlte) Kunden. Reihenfolge je Kunde
          nach execution_order; Kunden nacheinander in Prioritaetsreihenfolge.
        </p>
      </header>

      <Stepper
        steps={STEPS}
        active={step}
        completed={completed}
        onChange={(id) => setStep(id)}
        ariaLabel="Schritte der Ausrollung"
      />

      {step === 'routines' && (
        <RoutinesStep
          loading={routinesQuery.isLoading}
          error={routinesQuery.error}
          routines={routines}
          selected={selectedRoutineSlugs}
          onToggle={toggleRoutine}
          onNext={goNext}
        />
      )}

      {step === 'customers' && (
        <CustomersStep
          loading={customersQuery.isLoading}
          error={customersQuery.error}
          customers={activeCustomers}
          customerBySlug={customerBySlug}
          allCustomers={allCustomers}
          onAllCustomersChange={(v) => {
            setAllCustomers(v);
            setPreview(null);
          }}
          prioritySlugs={prioritySlugs}
          onToggleCustomer={toggleCustomer}
          onMove={movePriority}
          excludeCustomerRaw={excludeCustomerRaw}
          onExcludeCustomerRawChange={(v) => {
            setExcludeCustomerRaw(v);
            setPreview(null);
          }}
          onPrev={goPrev}
          onNext={goNext}
        />
      )}

      {step === 'start' && (
        <StartStep
          canPreview={canPreview}
          preview={preview}
          previewPending={previewMut.isPending}
          previewError={previewMut.error}
          excludeSystemIds={excludeSystemIds}
          onToggleExclude={toggleExcludeSystem}
          onPreview={loadPreview}
          checkMode={checkMode}
          onCheckModeChange={setCheckMode}
          snapshot={snapshot}
          onSnapshotChange={setSnapshot}
          requiresSnapshot={requiresSnapshot}
          destructive={destructive}
          waiverReason={waiverReason}
          onWaiverReasonChange={setWaiverReason}
          waiverEvidence={waiverEvidence}
          onWaiverEvidenceChange={setWaiverEvidence}
          idempotencyKey={idempotencyKey}
          onPrev={goPrev}
          onStartCheck={() => startRollout(true)}
          onStartApply={() => {
            setConfirmOpen(true);
          }}
          starting={startMut.isPending}
          startError={startMut.error}
        />
      )}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Echtlauf starten?"
        {...(preview
          ? {
              description: `${preview.totals.runs} Läufe bei ${preview.totals.customers_with_runs} Kunden – wirklich starten?`,
            }
          : {})}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={() => startRollout(false)}
              loading={startMut.isPending}
              data-testid="rollout-confirm-apply"
            >
              Wirklich starten
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Der Echtlauf ändert reale Systeme. Dry-Run zuerst, wenn unsicher.
        </p>
      </Dialog>
    </div>
  );
}

function RoutinesStep({
  loading,
  error,
  routines,
  selected,
  onToggle,
  onNext,
}: {
  loading: boolean;
  error: Error | null;
  routines: Routine[];
  selected: string[];
  onToggle: (code: string) => void;
  onNext: () => void;
}): JSX.Element {
  return (
    <Card
      title="Routinen"
      description="Reihenfolge je Kunde nach execution_order; Kunden nacheinander in Prioritätsreihenfolge."
    >
      {loading && <SkeletonRows rows={4} cols={3} />}
      {error && <ErrorAlert error={error} />}
      {!loading && !error && (
        <ul className="flex flex-col gap-2" data-testid="rollout-routines">
          {routines.map((r) => (
            <li key={r.id}>
              <label className="flex cursor-pointer items-center gap-3 rounded-md border border-border p-3 text-sm hover:bg-surface-2">
                <input
                  type="checkbox"
                  checked={selected.includes(r.code)}
                  onChange={() => onToggle(r.code)}
                  className="size-4 accent-primary"
                  data-testid={`rollout-routine-${r.code}`}
                />
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full border border-border bg-surface-2 text-xs font-semibold tabular-nums">
                  {r.execution_order}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{r.display_name}</span>
                  <code className="ml-2 font-mono text-xs text-muted">{r.code}</code>
                </span>
                <RoutineTierBadge tier={r.tier} />
              </label>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-sm text-muted">
        Reihenfolge je Kunde nach execution_order; Kunden nacheinander in
        Prioritaetsreihenfolge.
      </p>
      <footer className="mt-4 flex justify-end">
        <Button
          rightIcon={<ArrowRight className="size-4" />}
          disabled={selected.length === 0}
          onClick={onNext}
          data-testid="rollout-routines-next"
        >
          Weiter ({selected.length})
        </Button>
      </footer>
    </Card>
  );
}

function CustomersStep({
  loading,
  error,
  customers,
  customerBySlug,
  allCustomers,
  onAllCustomersChange,
  prioritySlugs,
  onToggleCustomer,
  onMove,
  excludeCustomerRaw,
  onExcludeCustomerRawChange,
  onPrev,
  onNext,
}: {
  loading: boolean;
  error: Error | null;
  customers: Customer[];
  customerBySlug: Map<string, Customer>;
  allCustomers: boolean;
  onAllCustomersChange: (v: boolean) => void;
  prioritySlugs: string[];
  onToggleCustomer: (slug: string) => void;
  onMove: (slug: string, dir: -1 | 1) => void;
  excludeCustomerRaw: string;
  onExcludeCustomerRawChange: (v: string) => void;
  onPrev: () => void;
  onNext: () => void;
}): JSX.Element {
  const canNext = allCustomers || prioritySlugs.length > 0;
  return (
    <Card title="Kunden" description="Auswahl und Priorität. Ohne Drag&Drop: Pfeile reichen.">
      {loading && <SkeletonRows rows={4} cols={2} />}
      {error && <ErrorAlert error={error} />}
      {!loading && !error && (
        <>
          <label className="mb-3 flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={allCustomers}
              onChange={(e) => onAllCustomersChange(e.target.checked)}
              className="size-4 accent-primary"
              data-testid="rollout-all-customers"
            />
            Alle Kunden (alphabetisch)
          </label>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="mb-2 text-xs uppercase tracking-wider text-muted">Aktive Kunden</p>
              <ul className="flex max-h-80 flex-col gap-1 overflow-y-auto" data-testid="rollout-customers">
                {customers.map((c) => (
                  <li key={c.id}>
                    <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-surface-2">
                      <input
                        type="checkbox"
                        checked={prioritySlugs.includes(c.slug)}
                        disabled={allCustomers}
                        onChange={() => onToggleCustomer(c.slug)}
                        className="size-4 accent-primary"
                        data-testid={`rollout-customer-${c.slug}`}
                      />
                      <span>{c.name}</span>
                      <code className="font-mono text-xs text-muted">{c.slug}</code>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="mb-2 text-xs uppercase tracking-wider text-muted">
                Priorität (oben zuerst)
              </p>
              {allCustomers ? (
                <p className="text-sm text-muted">
                  Alle aktiven Kunden, alphabetisch serverseitig.
                </p>
              ) : (
                <ol className="flex flex-col gap-1" data-testid="rollout-priority">
                  {prioritySlugs.map((slug, idx) => {
                    const c = customerBySlug.get(slug);
                    return (
                      <li
                        key={slug}
                        className="flex items-center gap-2 rounded-md border border-border px-2 py-1"
                        data-testid={`rollout-priority-${slug}`}
                      >
                        <span className="w-5 text-xs tabular-nums text-muted">{idx + 1}</span>
                        <span className="min-w-0 flex-1 truncate text-sm">
                          {c?.name ?? slug}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`${c?.name ?? slug} nach oben`}
                          disabled={idx === 0}
                          onClick={() => onMove(slug, -1)}
                          data-testid={`rollout-priority-up-${slug}`}
                        >
                          <ArrowUp className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`${c?.name ?? slug} nach unten`}
                          disabled={idx === prioritySlugs.length - 1}
                          onClick={() => onMove(slug, 1)}
                          data-testid={`rollout-priority-down-${slug}`}
                        >
                          <ArrowDown className="size-4" />
                        </Button>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </div>

          <div className="mt-4">
            <Input
              label="Kunden ausschließen (Slugs)"
              hint="Optional, kommagetrennt. Wirkt zusätzlich zur Auswahl."
              value={excludeCustomerRaw}
              onChange={(e) => onExcludeCustomerRawChange(e.target.value)}
              placeholder="kunde-a, kunde-b"
              data-testid="rollout-exclude-customers"
            />
          </div>
        </>
      )}
      <footer className="mt-4 flex justify-between">
        <Button variant="secondary" leftIcon={<ArrowLeft className="size-4" />} onClick={onPrev}>
          Zurück
        </Button>
        <Button
          rightIcon={<ArrowRight className="size-4" />}
          disabled={!canNext}
          onClick={onNext}
          data-testid="rollout-customers-next"
        >
          Weiter
        </Button>
      </footer>
    </Card>
  );
}

function StartStep({
  canPreview,
  preview,
  previewPending,
  previewError,
  excludeSystemIds,
  onToggleExclude,
  onPreview,
  checkMode,
  onCheckModeChange,
  snapshot,
  onSnapshotChange,
  requiresSnapshot,
  destructive,
  waiverReason,
  onWaiverReasonChange,
  waiverEvidence,
  onWaiverEvidenceChange,
  idempotencyKey,
  onPrev,
  onStartCheck,
  onStartApply,
  starting,
  startError,
}: {
  canPreview: boolean;
  preview: RolloutPreviewResponse | null;
  previewPending: boolean;
  previewError: Error | null;
  excludeSystemIds: string[];
  onToggleExclude: (id: string) => void;
  onPreview: () => void;
  checkMode: boolean;
  onCheckModeChange: (v: boolean) => void;
  snapshot: boolean;
  onSnapshotChange: (v: boolean) => void;
  requiresSnapshot: boolean;
  destructive: boolean;
  waiverReason: string;
  onWaiverReasonChange: (v: string) => void;
  waiverEvidence: string;
  onWaiverEvidenceChange: (v: string) => void;
  idempotencyKey: string;
  onPrev: () => void;
  onStartCheck: () => void;
  onStartApply: () => void;
  starting: boolean;
  startError: Error | null;
}): JSX.Element {
  const effectiveSnapshot = checkMode ? false : snapshot;
  const noSnapshotWarning = !checkMode && !effectiveSnapshot;
  const overridesRoutine = noSnapshotWarning && (requiresSnapshot || destructive);

  return (
    <Card title="Vorschau und Start" description="Erst planen, dann starten.">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button
          onClick={onPreview}
          disabled={!canPreview}
          loading={previewPending}
          data-testid="rollout-preview"
        >
          Vorschau
        </Button>
        {!canPreview && (
          <span className="text-xs text-muted">Routinen und Kunden zuerst wählen.</span>
        )}
      </div>

      {previewError && <ErrorAlert error={previewError} />}
      {previewPending && !preview && <SkeletonRows rows={3} cols={3} />}
      {preview && (
        <RolloutPlanTable
          preview={preview}
          excludeSystemIds={excludeSystemIds}
          onToggleExclude={onToggleExclude}
        />
      )}

      <fieldset className="mt-6 flex flex-col gap-3">
        <legend className="text-sm font-medium">Start</legend>
        <div className="rounded-md border border-border p-3 text-sm">
          <label htmlFor="rollout-check-mode" className="flex cursor-pointer items-start gap-3">
            <input
              id="rollout-check-mode"
              type="checkbox"
              checked={checkMode}
              onChange={(e) => onCheckModeChange(e.target.checked)}
              className="mt-0.5 size-4 accent-primary"
              data-testid="rollout-check-mode"
            />
            <span className="font-medium">Dry-Run (check_mode)</span>
          </label>
          <p className="mt-1 pl-7 text-xs text-muted">
            An = keine Aenderung am System. Default an, damit ein Klick nicht
            versehentlich echt laeuft.
          </p>
        </div>

        <label
          htmlFor="rollout-snapshot"
          className={[
            'flex items-start gap-3 rounded-md border border-border p-3 text-sm',
            checkMode ? 'opacity-60' : '',
          ].join(' ')}
        >
          <input
            id="rollout-snapshot"
            type="checkbox"
            checked={effectiveSnapshot}
            disabled={checkMode}
            onChange={(e) => onSnapshotChange(e.target.checked)}
            className="mt-0.5 size-4 accent-primary"
            data-testid="rollout-snapshot"
          />
          <span>
            <span className="inline-flex items-center gap-1.5 font-medium">
              <Camera className="size-4" aria-hidden="true" />
              Pre-Snapshot
            </span>
            {destructive && !checkMode && (
              <Badge tone="danger" className="ml-2">
                Pflicht (destruktiv) – Beleg beim Verzicht empfohlen
              </Badge>
            )}
          </span>
        </label>

        {overridesRoutine && (
          <div className="flex flex-col gap-3 rounded-md border border-border p-3" data-testid="waiver-form">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">
                Begründung für den Snapshot-Verzicht{' '}
                <span className="text-muted">(optional)</span>
              </span>
              <textarea
                value={waiverReason}
                onChange={(e) => onWaiverReasonChange(e.target.value)}
                rows={2}
                placeholder={VERZICHT_BEISPIEL}
                className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
                data-testid="waiver-reason"
              />
              <span className="text-xs text-muted">
                Optional. Warum ist hier kein Snapshot möglich oder sinnvoll? Steht danach am Lauf und im Wartungsbericht.
              </span>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">
                Ersatzsicherungs-Beleg{' '}
                {destructive ? (
                  <span className="text-warning">(empfohlen bei destruktiver Routine)</span>
                ) : (
                  <span className="text-muted">(optional)</span>
                )}
              </span>
              <Input
                value={waiverEvidence}
                onChange={(e) => onWaiverEvidenceChange(e.target.value)}
                placeholder={BELEG_BEISPIEL}
                data-testid="waiver-evidence"
              />
              <span className="text-xs text-muted">
                Optional. Was sichert diesen Stand stattdessen, z. B. PBS-Backup mit Zeitpunkt?
              </span>
            </label>
          </div>
        )}

        <p className="font-mono text-xs text-muted">Idempotency-Key: {idempotencyKey}</p>
      </fieldset>

      {startError && <ErrorAlert error={startError} />}

      <footer className="mt-4 flex flex-wrap justify-between gap-2">
        <Button variant="secondary" leftIcon={<ArrowLeft className="size-4" />} onClick={onPrev}>
          Zurück
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={onStartCheck}
            loading={starting}
            disabled={!preview}
            data-testid="rollout-start-check"
          >
            Dry-Run starten
          </Button>
          <Button
            variant="danger"
            leftIcon={
              destructive ? (
                <ShieldAlert className="size-4" />
              ) : (
                <PlayCircle className="size-4" />
              )
            }
            onClick={onStartApply}
            loading={starting}
            disabled={!preview}
            data-testid="rollout-start-apply"
          >
            Echtlauf starten
          </Button>
        </div>
      </footer>
      {!preview && (
        <p className="mt-2 inline-flex items-center gap-1 text-xs text-muted">
          <AlertTriangle className="size-3" aria-hidden="true" />
          Start erst nach einer Vorschau.
        </p>
      )}
    </Card>
  );
}
